import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import { config } from '../config';

interface AuthState {
  initialized: boolean;
  authenticated: boolean;
  token: string | undefined;
  userName: string | undefined;
  hasRole: (role: string) => boolean;
  login: () => void;
  logout: () => void;
  error: boolean;
  /** Lazily initialize Keycloak. No-op if already initialized. */
  init: () => void;
}

const AuthContext = createContext<AuthState>({
  initialized: false,
  authenticated: false,
  token: undefined,
  userName: undefined,
  hasRole: () => false,
  login: () => {},
  logout: () => {},
  error: false,
  init: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const keycloak = new Keycloak({
  url: config.keycloakUrl,
  realm: config.keycloakRealm,
  clientId: config.keycloakClientId,
});

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(false);
  const initStarted = useRef(false);

  const init = useCallback(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: undefined,
        checkLoginIframe: false,
      })
      .then((auth) => {
        setAuthenticated(auth);
        setInitialized(true);
      })
      .catch(() => {
        setError(true);
        setInitialized(true);
      });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        setAuthenticated(false);
      });
    };
  }, []);

  function hasRole(role: string): boolean {
    return keycloak.hasResourceRole(role, config.keycloakClientId);
  }

  function login() {
    keycloak.login();
  }

  function logout() {
    keycloak.logout({ redirectUri: window.location.origin + config.basePath });
  }

  const userName =
    keycloak.tokenParsed?.preferred_username ||
    keycloak.tokenParsed?.name ||
    keycloak.tokenParsed?.email;

  return (
    <AuthContext.Provider
      value={{
        initialized,
        authenticated,
        token: keycloak.token,
        userName,
        hasRole,
        login,
        logout,
        error,
        init,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
